import { useCallback, useRef, useState } from 'react';
import { DrilldownCtx } from './useDrilldown';

// Generic "click a summary row, land on the underlying records" primitive —
// deliberately NOT accounting-specific. A balance sheet line, a trial
// balance row, a report pivot cell, a dashboard stat tile, or any other
// summary view can call requestDrilldown(entity, filters, recordId) to ask
// the app to switch to a table view over `entity` pre-filtered by `filters`
// (and, optionally, land with one specific record's detail drawer open).
//
// `filters` uses the exact { field, op, value } shape FilterBar's own rules
// and ReportView's self-drilldown (`drillFilters`) already use — no new
// filter vocabulary to learn.
//
// `filters` IS carried in the URL: hashNav.js encodes it into the `?f=`
// query segment (see that file's own docstring), so a drilled-down view
// survives a refresh and a shared link reproduces the same filtered table
// the click produced. `recordId` likewise rides along in hashNav's
// positional recordId segment, so a drilldown landing on one specific
// record (e.g. GlobalSearch's result picker) is bookmarkable the same way
// clicking that row ordinarily would be.
//
// This comment previously said the opposite — that `filters` was NOT wired
// into the URL and stayed in-session-only, lost on refresh. That stopped
// being true when `?f=` landed, and the stale text survived because nothing
// asserted the behaviour end to end:
// e2e/tests/150-deep-link-roundtrip.spec.js only ever tested a hand-written
// `?f=` payload, with no view actually producing one. Row #19
// (200-financial-statements-drilldown.spec.js) closes that by drilling from
// a real TrialBalanceView row and reloading the resulting URL.
//
// Mounted once near the root (main.jsx), alongside ToastProvider/
// ConfirmProvider — same context+hook split for the same react-refresh
// reason (see useToast.js/ToastContext.jsx).
export function DrilldownProvider({ children }) {
  const [pending, setPending] = useState(null); // { entity, filters, recordId, viewIdx, nonce } | null
  const nonceRef = useRef(0);

  // nonce lets the same (entity, filters) pair request a second time (e.g.
  // clicking the same account row twice) still trigger LiveApp's consumer
  // effect, which keys off `pending` changing.
  // `viewIdx` (optional, last) pins a SPECIFIC view rather than letting the
  // consumer resolve one from `entity`. Two views can share an entity ("All
  // Issues" and "My Issues"), so entity-resolution can't express "take me to
  // that one" — which is what GlobalSearch's view results need. Everything
  // else keeps calling this with three arguments and behaves identically.
  const requestDrilldown = useCallback((entity, filters, recordId, viewIdx) => {
    setPending({
      entity,
      filters: filters || [],
      recordId: recordId || null,
      viewIdx: Number.isInteger(viewIdx) ? viewIdx : null,
      nonce: ++nonceRef.current,
    });
  }, []);

  const clearDrilldown = useCallback(() => setPending(null), []);

  return (
    <DrilldownCtx.Provider value={{ pending, requestDrilldown, clearDrilldown }}>
      {children}
    </DrilldownCtx.Provider>
  );
}
